import { useCloudOrder } from "@/hooks/useCloudOrder";
import React, { useState, useMemo, useRef, useEffect, Suspense } from "react";
import { Search, Grid3X3, Table2, X, MapPinned } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import type { NodeBasicInfo } from "@/contexts/NodeListContext";
import type { LiveData } from "../types/LiveData";
import { NodeGrid } from "./Node";
import Flag from "./Flag";
const NodeTable = React.lazy(() => import("./NodeTable"));
import { getRegionDisplayName, isRegionMatch } from "@/utils/regionHelper";
import "./NodeDisplay.css";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

export type ViewMode = "grid" | "table";


const getGroupLabel = (group: string, t: (key: string, opts?: any) => string) => {
  const map: Record<string, string> = {
    all: t('groups.all', { defaultValue: '全部' }),
    '主力': t('groups.primary', { defaultValue: '主力' }),
    '大善人': t('groups.sponsor', { defaultValue: '大善人' }),
    '玩具': t('groups.toy', { defaultValue: '玩具' }),
    '落地机': t('groups.edge', { defaultValue: '落地机' }),
  };
  return map[group] || group;
};


interface NodeDisplayProps {
  nodes: NodeBasicInfo[];
  liveData: LiveData;
}

const NodeDisplay: React.FC<NodeDisplayProps> = ({ nodes, liveData }) => {
  const [t, i18n] = useTranslation();
  const [viewMode, setViewMode] = useLocalStorage<ViewMode>(
    "nodeViewMode",
    "grid"
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroup, setSelectedGroup] = useLocalStorage<string>(
    "nodeSelectedGroup",
    "all"
  );
  const [groupByRegion, setGroupByRegion] = useLocalStorage<boolean>(
    "nodeGroupByRegion",
    false
  );
  const cloud = useCloudOrder();
  const activeOrder = cloud.state ? (viewMode === "grid" || cloud.state.follow ? cloud.state.grid : cloud.state.table) : [];
  const orderedNodes = useMemo(() => {
    const rank = new Map(activeOrder.map((id, i) => [id, i]));
    return [...nodes].sort((a, b) => (rank.get(a.uuid) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.uuid) ?? Number.MAX_SAFE_INTEGER) || a.weight - b.weight);
  }, [nodes, activeOrder]);
  const canSort = cloud.admin && Boolean(cloud.state) && (viewMode === "grid" || !cloud.state?.follow);
  const onOrder = canSort ? (ids: string[]) => { void cloud.save(viewMode === "grid" ? "grid" : "table", ids); } : undefined;
  const searchRef = useRef<HTMLInputElement>(null);

  // 获取所有的分组
  const groups = useMemo(() => {
    const groupSet = new Set<string>();
    nodes.forEach((node) => {
      if (node.group && node.group.trim()) {
        groupSet.add(node.group);
      }
    });
    return Array.from(groupSet).sort();
  }, [nodes]);

  // 判断是否显示分组选择器
  const showGroupSelector = groups.length >= 1;

  // 键盘快捷键支持
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 按 "/" 键聚焦搜索框
      if (e.key === "/" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        searchRef.current?.focus();
      }
      // 按 Escape 键清空搜索
      if (e.key === "Escape" && searchTerm) {
        setSearchTerm("");
        searchRef.current?.blur();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [searchTerm]);

  // 过滤节点
  const filteredNodes = useMemo(() => {
    let result = orderedNodes;

    // 先按分组过滤
    if (selectedGroup !== "all") {
      result = result.filter((node) => node.group === selectedGroup);
    }

    // 再按搜索条件过滤
    if (!searchTerm.trim()) return result;

    const term = searchTerm.toLowerCase().trim();
    return result.filter((node) => {
      // 基本信息搜索
      const basicMatch =
        node.name.toLowerCase().includes(term) ||
        node.os.toLowerCase().includes(term) ||
        node.arch.toLowerCase().includes(term);

      // 地区搜索（支持emoji和地区名称）
      const regionMatch = isRegionMatch(node.region, term);

      // 价格搜索（如果输入数字）
      const priceMatch =
        !isNaN(Number(term)) && node.price.toString().includes(term);

      // 状态搜索
      const isOnline = liveData?.online?.includes(node.uuid) || false;
      const statusMatch =
        ((term === "online" || term === "在线") && isOnline) ||
        ((term === "offline" || term === "离线") && !isOnline);

      return basicMatch || regionMatch || priceMatch || statusMatch;
    });
  }, [orderedNodes, searchTerm, liveData, selectedGroup]);

  const regionSections = useMemo(() => {
    const sections = new Map<string, NodeBasicInfo[]>();

    filteredNodes.forEach((node) => {
      const region = node.region?.trim() || "__unknown__";
      const section = sections.get(region);
      if (section) {
        section.push(node);
      } else {
        sections.set(region, [node]);
      }
    });

    return Array.from(sections, ([region, sectionNodes]) => ({
      region,
      nodes: sectionNodes,
      name:
        region === "__unknown__"
          ? t("common.unknownRegion", { defaultValue: "未设置地区" })
          : getRegionDisplayName(
              region,
              i18n.language.toLowerCase().startsWith("zh") ? "zh" : "en"
            ),
    }));
  }, [filteredNodes, i18n.language, t]);

  return (
    <div className="w-full space-y-6">
      <div className="ds-home-toolbar ds-home-toolbar-quad">
        <div className="ds-home-toolbar-cell ds-home-toolbar-cell-search">
          <div className="ds-home-toolbar-search group">
            <Search className="ds-home-toolbar-search-icon" />
            <Input
              ref={searchRef}
              placeholder={t("search.placeholder", {
                defaultValue: "按节点名称、地区、系统搜索…",
              })}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ds-home-toolbar-input"
            />
            {searchTerm && (
              <Button
                variant="ghost"
                size="icon"
                className="ds-home-toolbar-clear"
                onClick={() => {
                  setSearchTerm("");
                  searchRef.current?.focus();
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="ds-home-toolbar-cell ds-home-toolbar-cell-stat">
          <div className="ds-home-toolbar-stat">
            <div className="ds-home-toolbar-stat-dot" />
            {searchTerm.trim() ? (
              <span>
                {t("search.results", {
                  count: filteredNodes.length,
                  total:
                    selectedGroup === "all"
                      ? nodes.length
                      : nodes.filter((n) => n.group === selectedGroup).length,
                  defaultValue: `Found ${filteredNodes.length} nodes`,
                })}
              </span>
            ) : (
              <span>
                {selectedGroup === "all"
                  ? t("nodeCard.totalNodes", {
                      total: nodes.length,
                      online: liveData?.online?.length || 0,
                      defaultValue: `${liveData?.online?.length || 0} Online / ${nodes.length} Total`,
                    })
                  : t("nodeCard.groupNodes", {
                      group: getGroupLabel(selectedGroup, t),
                      total: filteredNodes.length,
                      online: filteredNodes.filter((n) => liveData?.online?.includes(n.uuid)).length,
                      defaultValue: `${filteredNodes.filter((n) => liveData?.online?.includes(n.uuid)).length} Online in ${selectedGroup}`,
                    })}
              </span>
            )}
          </div>
        </div>

        <div className="ds-home-toolbar-cell ds-home-toolbar-cell-group">
          <div className="ds-home-toolbar-region-controls">
            {showGroupSelector ? (
              <div className="ds-home-toolbar-group-shell">
                <div className="ds-home-toolbar-label">{t("common.group", { defaultValue: "分组" })}</div>
                <div className="ds-home-toolbar-tabs-wrap">
                  <Tabs value={selectedGroup} onValueChange={setSelectedGroup} className="w-auto">
                    <TabsList className="ds-home-toolbar-tabslist">
                      <TabsTrigger value="all" className="ds-home-toolbar-tabtrigger">
                        {getGroupLabel("all", t)}
                      </TabsTrigger>
                      {groups.map((group) => (
                        <TabsTrigger key={getGroupLabel(group, t)} value={getGroupLabel(group, t)} className="ds-home-toolbar-tabtrigger">
                          {getGroupLabel(group, t)}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>
                </div>
              </div>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              className={cn("ds-home-toolbar-regionbtn", groupByRegion && "is-active")}
              aria-pressed={groupByRegion}
              onClick={() => setGroupByRegion((enabled) => !enabled)}
            >
              <MapPinned className="h-4 w-4" />
              <span>{t("common.groupByRegion", { defaultValue: "按地区分区" })}</span>
            </Button>
          </div>
        </div>

        <div className="ds-home-toolbar-cell ds-home-toolbar-cell-view">
          <div className="ds-home-toolbar-viewswitch-row">
            <Button
              variant="ghost"
              size="sm"
              className={cn("ds-home-toolbar-viewbtn", viewMode === "grid" && "is-active")}
              onClick={() => setViewMode("grid")}
            >
              <Grid3X3 className="h-4 w-4" />
              <span>{t("common.grid", { defaultValue: "网格" })}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={cn("ds-home-toolbar-viewbtn", viewMode === "table" && "is-active")}
              onClick={() => setViewMode("table")}
            >
              <Table2 className="h-4 w-4" />
              <span>{t("common.table", { defaultValue: "表格" })}</span>
            </Button>
          </div>
        </div>
      </div>

      {viewMode === "table" && cloud.state ? (
        <div className="ds-cloud-order-controls">
          <label><input type="checkbox" checked={cloud.state.follow} disabled={!cloud.admin || cloud.busy} onChange={e => void cloud.save("follow", e.target.checked)} /> 跟随网格顺序</label>
        </div>
      ) : null}
      {/* Node Display Area */}
      {filteredNodes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-muted/20 rounded-lg border border-dashed">
          <span className="text-lg text-muted-foreground mb-2">
            {searchTerm.trim()
              ? t("search.no_results", { defaultValue: "No matching nodes found" })
              : t("nodes.empty", { defaultValue: "No node data" })}
          </span>
          {searchTerm.trim() && (
            <span className="text-sm text-muted-foreground">
              {t("search.try_different", {
                defaultValue: "Try different keywords",
              })}
            </span>
          )}
        </div>
      ) : (
        <>
          {groupByRegion ? (
            <div className="ds-region-sections">
              {regionSections.map((section) => (
                <section className="ds-region-section" key={section.region}>
                  <div className="ds-region-section-header">
                    {section.region === "__unknown__" ? (
                      <MapPinned className="ds-region-section-icon" aria-hidden="true" />
                    ) : (
                      <Flag flag={section.region} />
                    )}
                    <h2 className="ds-region-section-title">{section.name}</h2>
                    <span className="ds-region-section-count">
                      {t("nodeCard.regionNodeCount", {
                        count: section.nodes.length,
                        defaultValue: `${section.nodes.length} 台`,
                      })}
                    </span>
                  </div>
                  {viewMode === "grid" ? (
                    <NodeGrid sortingDisabled={cloud.busy} onOrder={onOrder} nodes={section.nodes} liveData={liveData} />
                  ) : (
                    <Suspense
                      fallback={<div className="p-4 text-center">{t("common.loading_table", { defaultValue: "正在加载表格..." })}</div>}
                    >
                      <NodeTable sortingDisabled={cloud.busy} onOrder={onOrder} nodes={section.nodes} liveData={liveData} />
                    </Suspense>
                  )}
                </section>
              ))}
            </div>
          ) : viewMode === "grid" ? (
            <NodeGrid sortingDisabled={cloud.busy} onOrder={onOrder} nodes={filteredNodes} liveData={liveData} />
          ) : (
            <Suspense
              fallback={<div className="p-4 text-center">{t("common.loading_table", { defaultValue: "正在加载表格..." })}</div>}
            >
              <NodeTable sortingDisabled={cloud.busy} onOrder={onOrder} nodes={filteredNodes} liveData={liveData} />
            </Suspense>
          )}
        </>
      )}
    </div>
  );
};

export default NodeDisplay;
